// src/features/reports/WprProjectReadOut.jsx
//
// WPR Slice 3 — Expandable per-project read-out section.
// Rendered below the roll-up table in WeeklyPortfolioReview.jsx.
//
// One section per ACTIVE project (phase !== "complete"), same membership
// and sort order as the roll-up.  Props are precomputed by
// buildWprReadOutProps() in wprFilters.js.
//
// WPR-SNAPSHOT: when `frozenTracks` and `frozenWeeklyUpdate` are provided
// (frozen-week mode), the component skips the lazy-load effect entirely and
// renders directly from snapshot data. This satisfies the architectural
// constraint that frozen reads must NEVER query live DB tables.
//
// Body (lazy-loaded on first expand, cached for the session):
//   1. BLOCKER callout — red banner when the latest weekly update has blockers
//   2. Tracks table   — reuses TracksPanel (Slice 1) for edit-back consistency.
//      Every save goes via updateProjectTrack() in projectsClient.js — the
//      SAME function TracksPanel on the Project detail page uses.  No second
//      write path.
//   3. Image slot     — STUB ONLY.  No upload / storage wiring in Slice 3.
//      Placeholder exists so the layout matches the mockup.
//      Slice 5 replaces this with real object-storage wiring.
//   4. Milestone footer
//
// Permission contract:
//   canEdit=false → TracksPanel shows read-only rows (no Edit buttons).
//   canEdit=true  → TracksPanel allows inline edits.
//   Viewer-role enforcement is at the API layer (requireRole("editor") on the
//   PATCH endpoint) — no additional UI-only guard needed here.

import React, { useState, useCallback, useEffect } from "react";
import { T } from "../../app/tokens.js";
import { TracksPanel } from "../projects/TracksPanel.jsx";
import {
  getProjectTracks,
  getProjectWeeklyUpdates,
} from "../../data/projectsClient.js";
import { log } from "../../utils/logger.js";
import { weeklyText } from "../../lib/weeklyRichText.js";
import { ReadOutStatusPill, ReadOutBlockerCallout, ReadOutMilestoneFooter } from "./WprReadOutCore.jsx";

// StatusPill / BlockerCallout / milestone footer live in the shared
// presentational core (WprReadOutCore.jsx), also used by Projects Weekly Overview.
const StatusPill = ReadOutStatusPill;
const BlockerCallout = ReadOutBlockerCallout;

// ─── ImageSlot — Slice 5 stub ──────────────────────────────────────────────────
// IMAGE ATTACHMENT STUB — Slice 3 only renders the disabled placeholder.
// DO NOT wire to object storage here.  Slice 5 replaces this element with
// a real upload affordance backed by the App Storage bucket.

function ImageSlot() {
  return (
    <div
      data-testid="wpr-readout-image-slot"
      style={{
        border:       `1px dashed ${T.border}`,
        borderRadius: 8,
        padding:      "16px 18px",
        margin:       "14px 0",
        display:      "flex",
        alignItems:   "center",
        gap:          12,
        background:   T.lightGray,
      }}
    >
      <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 4, border: `1px solid ${T.border}`, background: T.card, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.mid, marginBottom: 4 }}>
          Floor plans / test fits
        </div>
        <button
          type="button"
          disabled
          style={{
            fontSize: T.fsXs,
            padding:      "2px 10px",
            borderRadius: 4,
            background:   T.card,
            color:        T.light,
            border:       `1px solid ${T.border}`,
            cursor:       "not-allowed",
          }}
        >
          + Add image
        </button>
      </div>
    </div>
  );
}

// ─── WprProjectReadOut ────────────────────────────────────────────────────────

/**
 * Expandable read-out card for a single active project.
 *
 * @param {{
 *   project: {
 *     projectId:     string,
 *     name:          string,
 *     internalOwner: string,
 *     ragColor:      string,
 *     statusLabel:   string,
 *     statusBg:      string,
 *     milestone:     string,
 *   },
 *   canEdit:            boolean,
 *   frozenTracks?:      object[] | null,  — snapshot tracks (frozen mode only)
 *   frozenWeeklyUpdate?: object | null,   — snapshot weekly update (frozen mode only)
 * }} props
 */
export function WprProjectReadOut({ project, canEdit, frozenTracks = null, frozenWeeklyUpdate = null }) {
  const isFrozen = frozenTracks !== null; // frozen mode when snapshot data is supplied

  const [open,        setOpen]       = useState(false);
  const [loaded,      setLoaded]     = useState(isFrozen); // frozen mode: pre-loaded
  const [loading,     setLoading]    = useState(false);
  const [tracks,      setTracks]     = useState(isFrozen ? (frozenTracks ?? []) : []);
  const [blockerText, setBlocker]    = useState(isFrozen ? (frozenWeeklyUpdate?.blockers ?? null) : null);
  const [loadError,   setLoadError]  = useState(null);

  // When transitioning between frozen/live for the same project, reset state.
  // This handles the case where an admin unfreezes a week.
  useEffect(() => {
    if (isFrozen) {
      setTracks(frozenTracks ?? []);
      setBlocker(frozenWeeklyUpdate?.blockers ?? null);
      setLoaded(true);
      setLoading(false);
      setLoadError(null);
    } else {
      // Switched to live mode — reset to force a re-fetch on next expand.
      setTracks([]);
      setBlocker(null);
      setLoaded(false);
      setLoadError(null);
    }
  }, [isFrozen, frozenTracks, frozenWeeklyUpdate]);

  // Lazy-load tracks + latest weekly update on first expand (live mode only).
  // Results are cached in component state for the session (loaded=true guard).
  useEffect(() => {
    if (isFrozen) return; // frozen mode: data already loaded from snapshot
    if (!open || loaded) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    Promise.all([
      getProjectTracks(project.projectId),
      getProjectWeeklyUpdates(project.projectId),
    ])
      .then(([tks, updates]) => {
        if (cancelled) return;
        setTracks(Array.isArray(tks) ? tks : []);
        const latest = Array.isArray(updates) && updates.length > 0 ? updates[0] : null;
        setBlocker(latest?.blockers || null);
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("WprProjectReadOut: load failed", {
          projectId: project.projectId,
          err: String(err),
        });
        setLoadError("Failed to load project details - try expanding again.");
        setLoaded(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isFrozen, open, loaded, project.projectId]);

  // Called by TracksPanel after a successful PATCH.
  // Merges the updated track into local state so the UI reflects the change
  // immediately — same pattern TracksPanel uses internally.
  const handleTrackUpdated = useCallback((updated) => {
    setTracks((prev) => {
      const next = prev.filter((t) => t.trackKey !== updated.trackKey);
      return [...next, updated];
    });
  }, []);

  return (
    <div
      data-testid={`wpr-readout-${project.projectId}`}
      style={{
        border:       `1px solid ${T.border}`,
        borderRadius: 8,
        background:   T.card,
        overflow:     "hidden",
      }}
    >
      {/* ── Header row (always visible) ── */}
      <button
        type="button"
        aria-expanded={open}
        data-testid="wpr-readout-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          width:          "100%",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          padding:        "12px 16px",
          background:     open ? T.lightGray : T.card,
          border:         "none",
          cursor:         "pointer",
          textAlign:      "left",
          gap:            12,
          transition:     "background 0.1s ease",
        }}
      >
        <div style={{
          display:    "flex",
          alignItems: "center",
          gap:        10,
          minWidth:   0,
          flex:       1,
        }}>
          <StatusPill
            label={project.statusLabel}
            color={project.ragColor}
            bg={project.statusBg}
          />
          <span style={{
            fontWeight: T.fwBold,
            fontSize: T.fsMd,
            color:        T.dark,
            overflow:     "hidden",
            textOverflow: "ellipsis",
            whiteSpace:   "nowrap",
          }}>
            {project.name}
          </span>
          <span style={{ fontSize: T.fsSm, color: T.mid, whiteSpace: "nowrap" }}>
            {project.internalOwner}
          </span>
        </div>
        <span style={{
          fontSize: T.fsLg,
          color:      T.mid,
          flexShrink: 0,
          transform:  open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.15s ease",
          lineHeight: 1,
        }}>
          ▾
        </span>
      </button>

      {/* ── Expanded body ── */}
      {open && (
        <div style={{ padding: "14px 16px 18px", borderTop: `1px solid ${T.border}` }}>

          {loading && (
            <div style={{ padding: "10px 0", color: T.mid, fontSize: T.fsBase }}>
              Loading…
            </div>
          )}

          {loadError && !loading && (
            <div style={{ padding: "6px 0", color: T.red, fontSize: T.fsSm }}>
              {loadError}
            </div>
          )}

          {loaded && !loadError && (
            <>
              {/* 1. Blocker callout — only when the latest weekly update has blockers */}
              {blockerText && <BlockerCallout text={weeklyText(blockerText)} />}

              {/* 2. Tracks table — reuses Slice 1 TracksPanel for single write path */}
              <TracksPanel
                projectId={project.projectId}
                tracks={tracks}
                onTrackUpdated={handleTrackUpdated}
                canEdit={canEdit}
              />

              {/* 3. Image slot — STUB only; Slice 5 wires this to object storage */}
              <ImageSlot />

              {/* 4. Milestone footer */}
              <ReadOutMilestoneFooter milestone={project.milestone} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
